import { useEffect, useMemo } from "react";
import { Select, Row, Col, Typography } from "antd";
import dayjs from "dayjs";
import { EventInfoTextConstants } from "./constants";
import { useEventContext } from "../../../context/EventContext/EventContext";
import { useEventTagContext } from "../../../context/EventTagContext/EventTagContext";
import { cultures, EventTextRules } from "../../../context/EventContext/constants";
import SwitchOptionComponent from "../../../components/SwitchComponent";
import InputComponent from "../../../components/InputComponent";
import DatePickerComponent from "../../../components/DatePickerComponent";
import InputNumberComponent from "../../../components/InputNumberComponent";

const EventInfo = () => {
	const { eventEditForm: eventUpdateForm, setEventEditForm: setEventUpdateForm } = useEventContext();

	if (!eventUpdateForm) {
		return null;
	}

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={12} md={8} lg={6}>
					<SwitchOptionComponent
						title={EventInfoTextConstants.isActive.label}
						helperText={EventInfoTextConstants.isActive.helperText}
						value={eventUpdateForm.isActive}
						onChange={checked => setEventUpdateForm(prev => prev ? { ...prev, isActive: checked } : null)}
					/>
				</Col>
				<Col xs={24} sm={12} md={12} lg={8}>
					<SwitchOptionComponent
						title={EventInfoTextConstants.isFeatured.label}
						helperText={EventInfoTextConstants.isFeatured.helperText}
						value={eventUpdateForm.eventPreview.isFeatured}
						onChange={checked =>
							setEventUpdateForm(prev => prev ? { ...prev, eventPreview: { ...prev.eventPreview, isFeatured: checked } } : null)
						}
					/>	
				</Col>
			</Row>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={12} md={6} lg={4}>
					<InputNumberComponent
						title={EventInfoTextConstants.displayOrder.label}
						positiveOnly
						value={eventUpdateForm.eventPreview.displayOrder}
						placeholder={EventInfoTextConstants.displayOrder.placeholder}
						onChange={value =>
							setEventUpdateForm(prev => prev ? ({
								...prev,
								eventPreview: {
									...prev.eventPreview,
									displayOrder: value === null || value === "" ? undefined : Number(value),
								},
							}) : null)
						}
					/>
				</Col>
			</Row>
		</>
	);
};

export const EventTags = () => {
	const { eventEditForm: eventUpdateForm, setEventEditForm: setEventUpdateForm } = useEventContext();

	if (!eventUpdateForm) {
		return null;
	}
	const { eventTagList, getEventTagList } = useEventTagContext();

	useEffect(() => {
		getEventTagList();
	}, []);

	const tagOptions = useMemo(
		() => eventTagList.map(tag => ({ label: tag.tagName, value: tag.eventTagId })),
		[eventTagList]
	);

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={24} md={12} lg={12}>
					<InputComponent
						title={EventInfoTextConstants.eventTags.locationLabel}
						placeholder={EventInfoTextConstants.eventTags.locationPlaceholder}
						value={eventUpdateForm.locationText}
						maxLength={EventTextRules.locationTextMaxLength}
						onChange={e => setEventUpdateForm(prev => prev ? { ...prev, locationText: e.target.value } : null)}
					/>
				</Col>
				<Col xs={24} sm={24} md={12} lg={12}>
					<InputComponent
						title={EventInfoTextConstants.eventTags.projectDateTextLabel}
						placeholder={EventInfoTextConstants.eventTags.projectDateTextPlaceholder}
						value={eventUpdateForm.projectDateText}
						maxLength={EventTextRules.projectDateTextMaxLength}
						onChange={e => setEventUpdateForm(prev => prev ? { ...prev, projectDateText: e.target.value } : null)}
					/>
				</Col>
				<Col xs={24} sm={24} md={12} lg={10}>
					<Typography.Text>{EventInfoTextConstants.eventTags.tagsLabel}</Typography.Text>
					<Select
						mode="multiple"
						size="large"
						style={{ width: "100%" }}
						placeholder={EventInfoTextConstants.eventTags.tagsPlaceholder}
						maxCount={EventInfoTextConstants.eventTags.maxTagCount}
						options={tagOptions}
						value={eventUpdateForm.eventTagIds}
						onChange={(values: string[]) => setEventUpdateForm(prev => prev ? { ...prev, eventTagIds: values } : null)}
						optionFilterProp="label"
					/>
				</Col>
			</Row>
		</>
	);
};

export const AppliableEventSettings = () => {
	const { eventEditForm: eventUpdateForm, setEventEditForm: setEventUpdateForm } = useEventContext();

	if (!eventUpdateForm) {
		return null;
	}

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={24} md={12} lg={12}>
					<DatePickerComponent
						title={EventInfoTextConstants.appliableSettings.projectDateLabel}
						allowClear
						value={eventUpdateForm.projectDate ? dayjs(eventUpdateForm.projectDate) : null}
						onChange={date => setEventUpdateForm(prev => prev ? { ...prev, projectDate: date || null } : null)}
					/>
				</Col>
				<Col xs={24} sm={24} md={12} lg={12}>
					<SwitchOptionComponent
						title={EventInfoTextConstants.appliableSettings.applicationStatusLabel}
						helperText={EventInfoTextConstants.appliableSettings.applicationStatusHelperText}
						value={eventUpdateForm.isAppliable}
						onChange={checked => setEventUpdateForm(prev => prev ? { ...prev, isAppliable: checked } : null)}
					/>
				</Col>
			</Row>
		</>
	);
};

export const NotAppliableEventSettings = () => {
	const { eventEditForm: eventUpdateForm, setEventEditForm: setEventUpdateForm } = useEventContext();

	if (!eventUpdateForm) {
		return null;
	}

	const trCulture = cultures[0];
	const enCulture = cultures[1];
	const trCategoryTag = eventUpdateForm.eventPreview.eventPreviewLocalizable.find((x: any) => x.culture === trCulture)?.categoryTag ?? "";
	const enCategoryTag = eventUpdateForm.eventPreview.eventPreviewLocalizable.find((x: any) => x.culture === enCulture)?.categoryTag ?? "";

	const onCategoryTagChange = (culture: string, value: string) => {
		setEventUpdateForm(prev => prev ? ({
			...prev,
			categoryTag: culture === trCulture ? value : prev.eventPreview.eventPreviewLocalizable.find((x: any) => x.culture === culture)?.categoryTag ?? "",
			eventPreview: {
				...prev.eventPreview,
				eventPreviewLocalizable: prev.eventPreview.eventPreviewLocalizable.map((item: any) =>
					item.culture === culture ? { ...item, categoryTag: value } : item
				),
			},
		}) : null);
	};

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={24} md={8} lg={8}>
					<InputComponent
						title={EventInfoTextConstants.notAppliableSettings.categoryTagTrLabel}
						placeholder={EventInfoTextConstants.notAppliableSettings.categoryTagTrPlaceholder}
						value={trCategoryTag}
						maxLength={EventTextRules.categoryTagMaxLength}
						onChange={e => onCategoryTagChange(trCulture, e.target.value)}
					/>
				</Col>
				<Col xs={24} sm={24} md={8} lg={8}>
					<InputComponent
						title={EventInfoTextConstants.notAppliableSettings.categoryTagEnLabel}
						placeholder={EventInfoTextConstants.notAppliableSettings.categoryTagEnPlaceholder}
						value={enCategoryTag}
						maxLength={EventTextRules.categoryTagMaxLength}
						onChange={e => onCategoryTagChange(enCulture, e.target.value)}
					/>
				</Col>
				<Col xs={24} sm={24} md={8} lg={8}>
					<SwitchOptionComponent
						title={EventInfoTextConstants.notAppliableSettings.customButtonLabel}
						helperText={EventInfoTextConstants.notAppliableSettings.customButtonHelperText}
						value={eventUpdateForm.eventDetail.hasCustomButton}
						onChange={checked =>
							setEventUpdateForm(prev => prev ? { ...prev, eventDetail: { ...prev.eventDetail, hasCustomButton: checked } } : null)
						}
					/>
				</Col>
			</Row>
		</>
	);
};

export default EventInfo;
